// The Save menu borrows the shared dropdown's menu styles (see SaveMenu), so pull their stylesheets
// in here rather than relying on some other Dropdown on the page having loaded them.
import '@Components/Dropdown/BootstrapDropdown.scss';
import '@Components/Dropdown/DropdownMenu.scss';
import './ChartEditExits.scss';
import { ComponentProps, useContext, useState } from 'react';
import { Dropdown as BSDropdown } from 'react-bootstrap';
import DropdownContext from '@restart/ui/DropdownContext';
import cn from 'classnames';
import { Button } from '@Components/Button';
import { Appearance, Design, Size, StretchMode } from '@Components/designConstants';
import CaretIcon from '@Components/Dropdown/CaretIcon.svg?react';

interface ChartEditExitsProps {
  saving: boolean;
  onUpdate: () => void;
  onSaveAsNew: () => void;
  onDiscard: () => void;
}

// The exits for a SAVED chart carrying an unsaved edit, shared by the dashboard card and the
// single-chart view. Small-sized throughout, to sit as compactly as the card's other inline
// controls (e.g. the KPI picker). Grouped under an "Unsaved changes" label so they read as being
// about the pending edit rather than as more of the header's always-there controls, and spelled out:
// as icon-only buttons (✓ / ⧉ / ✕) users couldn't tell them apart without hovering. Both ways of
// saving sit under one Save menu, which states the choice that actually confuses people — change
// this chart everywhere, or fork a copy.
export const ChartEditExits = ({
  saving,
  onUpdate,
  onSaveAsNew,
  onDiscard
}: ChartEditExitsProps) => (
  <div className='chart-edit-exits'>
    <span className='chart-edit-exits-label'>Unsaved changes</span>
    <SaveMenu saving={saving} onUpdate={onUpdate} onSaveAsNew={onSaveAsNew} />
    <Button
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      disabled={saving}
      onClick={onDiscard}
    >
      Discard
    </Button>
  </div>
);

interface SaveMenuProps {
  saving: boolean;
  onUpdate: () => void;
  onSaveAsNew: () => void;
}

// The Save menu: a small brand button opening the same purple-bordered `dropdown-menu-container`
// the card's KPI picker uses, so it reads as one of the card's own controls. Not the shared
// Dropdown itself: that is a value picker (its toggle shows "Label: value"), and these rows are
// actions that each need a second line saying what they do. Same composition as DownloadDropdown:
// the toggle bypasses BSDropdown.Toggle, so it hands its own ref
// to Popper (see SaveToggle).
const SaveMenu = ({ saving, onUpdate, onSaveAsNew }: SaveMenuProps) => {
  const [show, setShow] = useState(false);

  return (
    <BSDropdown className={cn('bpm-dropdown', StretchMode.FIT)} show={show} onToggle={setShow}>
      <SaveToggle
        loading={saving}
        aria-haspopup='menu'
        aria-expanded={show}
        onClick={() => setShow(s => !s)}
      >
        Save
      </SaveToggle>
      <BSDropdown.Menu className='dropdown-menu-container chart-edit-exits-menu' align='end'>
        <SaveMenuItem
          label="Update everywhere it's used"
          description='Changes this chart on every dashboard it appears on'
          onClick={onUpdate}
        />
        <SaveMenuItem
          label='Save as a new chart'
          description='Keeps the original chart as it was'
          onClick={onSaveAsNew}
        />
      </BSDropdown.Menu>
    </BSDropdown>
  );
};

// Pulls react-bootstrap's setToggle ref out of DropdownContext so Popper can position the menu
// against this button (needed for align='end'). A brand Button rather than the dropdown's own
// toggle shell: DropdownToggle.scss paints every shell toggle as a white input field, and Save is
// the primary action here, so it stays purple. The caret is the dropdown's own, so it still reads
// as the same kind of control as the KPI picker.
const SaveToggle = (props: ComponentProps<typeof Button>) => {
  const ctx = useContext(DropdownContext);
  return (
    <Button
      {...props}
      ref={ctx?.setToggle}
      size={Size.SM}
      trailingElement={<CaretIcon className='chart-edit-exits-caret' />}
    />
  );
};

interface SaveMenuItemProps {
  label: string;
  description: string;
  onClick: () => void;
}

const SaveMenuItem = ({ label, description, onClick }: SaveMenuItemProps) => (
  <BSDropdown.Item as='button' onClick={onClick}>
    <span className='chart-edit-exits-menu-label'>{label}</span>
    <span className='chart-edit-exits-menu-description'>{description}</span>
  </BSDropdown.Item>
);
